import GetStartedButton from "@/components/Buttons/GetStartedButton";
import Container from "@/components/Container/Container";
import Layout from "@/components/Layout/Layout";
import {
  Body,
  BodyMedium14,
  BodyMedium14Css,
  BodyMedium20Css,
  H3Css,
  H5Css,
  Heading,
} from "@/components/Typography";
import { mq } from "@/constants/breakpoints";
import { darkColor } from "@/constants/colors";
import { css } from "@emotion/react";
import { GetStaticProps } from "next";
import Image from "next/image";
import Balancer from "react-wrap-balancer";
import readyShape from "../../public/images/demo/shape.svg";
import HelpSection from "@/components/About/HelpSection";
import { getDemoPage } from "@/utils/getDemoPage";
import { getSplitFAQs } from "@/utils/faqFunctions";
import FAQTitle from "@/components/FAQ/FAQTitle";
import faqIcon from "../../public/images/demo/faq-icon.svg";
import FAQItem from "@/components/FAQ/FAQItem";
import Script from "next/script";
import { useEffect } from "react";
import posthog from "posthog-js";

const WISTIA_ID = "mcw40hy9tp";

interface demoProps {
  fields: any;
}

const Page = ({ fields }: demoProps) => {
  useEffect(() => {
    // @ts-ignore
    window._wq = window._wq || [];
    // @ts-ignore
    window._wq.push({
      id: WISTIA_ID,
      onReady: (returnVideo: any) => {
        returnVideo.bind("play", () => {
          posthog.capture("JWMarketing - Played Demo Video");
        });
      },
    });
  }, []);

  const splitSubheading = fields.subheading.split("ORG_COUNT");

  const Subheading = () => (
    <>
      <span>{splitSubheading[0]}</span>
      <Body
        css={css`
          display: inline;
          font-weight: 800;
          font-size: 18px;
          line-height: 22.4px;
          ${mq["lg"]} {
            font-size: 24px;
            line-height: 33.6px;
          }
        `}
      >
        {process.env.organizationCount}+
      </Body>
      <span>{splitSubheading[1]}</span>
    </>
  );

  const { firstHalfFAQs, secondHalfFAQs } = getSplitFAQs(
    fields.faqItemsCollection.items
  );

  return (
    <>
      <Script src="https://fast.wistia.com/assets/external/E-v1.js" />
      <Layout
        title={fields.pageTitle}
        metaDescription={fields.metaDescription}
        heroContainerCss={css`
          background: url(/images/demo/BG.svg),
            linear-gradient(112.24deg, #048cdd 4.51%, #32b2ff 100.81%);
          background-size: cover;
          background-repeat: no-repeat;
        `}
        hero={
          <Container
            css={css`
              margin-top: 24px;
              ${mq["lg"]} {
                margin-top: 60px;
              }
            `}
          >
            <Heading
              level={1}
              css={css`
                color: #fff;
                text-align: center;
                max-width: 1100px;
                ${H5Css}
                ${mq["lg"]} {
                  ${H3Css}
                }
              `}
            >
              <Balancer>{fields.heading}</Balancer>
            </Heading>
            <Body
              css={css`
                color: #fff;
                margin-top: 14px;
                text-align: center;
                margin-bottom: 40px;
                max-width: 1100px;
                ${BodyMedium14Css}
                ${mq["lg"]} {
                  margin-top: 24px;
                  ${BodyMedium20Css}
                  margin-bottom: 70px;
                }
              `}
            >
              <Balancer>
                <Subheading />
              </Balancer>
            </Body>
            <div
              css={css`
                position: relative;
                padding-top: 28.125%;
                max-width: 1168px;
              `}
            >
              <div
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  z-index: 2;
                `}
              >
                <div
                  className="wistia_responsive_padding"
                  style={{
                    padding: "56.25% 0 0 0", // for 16:9 aspect ratio
                    position: "relative",
                  }}
                >
                  <div
                    className="wistia_responsive_wrapper"
                    style={{
                      height: "100%",
                      left: "0",
                      position: "absolute",
                      top: "0",
                      width: "100%",
                    }}
                  >
                    <div
                      className="wistia_embed wistia_async_mcw40hy9tp seo=false videoFoam=true"
                      style={{
                        height: "100%",
                        position: "relative",
                        width: "100%",
                      }}
                    >
                      <div
                        className="wistia_swatch"
                        style={{
                          height: "100%",
                          left: "0",
                          opacity: "1",
                          overflow: "hidden",
                          position: "absolute",
                          top: "0",
                          transition: "opacity 200ms",
                          width: "100%",
                        }}
                      >
                        <img
                          src="https://fast.wistia.com/embed/medias/mcw40hy9tp/swatch"
                          style={{
                            filter: "blur(5px)",
                            height: "100%",
                            objectFit: "contain",
                            width: "100%",
                          }}
                          alt="demo thumbnail"
                          aria-hidden="true"
                          // @ts-ignore
                          onload="this.parentNode.style.opacity=1;"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        }
      >
        <Container>
          <div
            css={css`
              position: relative;
              width: 100%;
              padding-top: 28.125%;
              background: #fff;
              z-index: 0;
            `}
          />
          <div
            css={css`
              margin-top: 60px;
              display: flex;
              flex-direction: column;
              gap: 50px;
              ${mq["sm"]} {
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
              }
            `}
          >
            <div>
              <Heading
                css={css`
                  color: ${darkColor};
                  text-align: center;
                  ${H5Css}
                  ${mq["sm"]} {
                    text-align: left;
                  }
                  ${mq["lg"]} {
                    ${H3Css}
                  }
                `}
              >
                Ready to Give it a Shot?
              </Heading>
              <BodyMedium14
                css={css`
                  color: ${darkColor};
                  margin-top: 14px;
                  text-align: center;
                  ${mq["sm"]} {
                    text-align: left;
                  }
                  ${mq["lg"]} {
                    margin-top: 24px;
                    ${BodyMedium20Css}
                  }
                `}
              >
                <Balancer>
                  Join over {process.env.organizationCount} sports organizations
                  using Jersey Watch to save time this season.
                </Balancer>
              </BodyMedium14>
              <div
                css={css`
                  margin: 40px auto 0;
                  a {
                    margin: auto;
                  }
                  ${mq["sm"]} {
                    a {
                      margin: unset;
                    }
                  }
                `}
              >
                <GetStartedButton />
              </div>
            </div>
            <div>
              <Image
                src={readyShape}
                alt="Demo Illustration"
                css={css`
                  width: 203px;
                  height: auto;
                  margin: auto;
                  display: block;
                  ${mq["sm"]} {
                    width: 230px;
                  }
                  ${mq["md"]} {
                    width: 300px;
                  }
                  ${mq["lg"]} {
                    width: 450px;
                  }
                `}
              />
            </div>
          </div>
        </Container>
        <Container
          css={css`
            margin-top: 77px;
          `}
        >
          <div>
            <FAQTitle icon={faqIcon} title="Frequently Asked Questions" />
            <div
              css={css`
                display: flex;
                width: 100%;
                flex-direction: column;
                ${mq["lg"]} {
                  margin-top: 40px;
                }
                ${mq["xl"]} {
                  flex-direction: row;
                  gap: 105px;
                }
              `}
            >
              <div
                css={css`
                  flex: 1;
                `}
              >
                {firstHalfFAQs.map((item: any, index: number) => (
                  <FAQItem
                    key={index}
                    question={item.question}
                    answer={item.answer.json}
                  />
                ))}
              </div>
              <div
                css={css`
                  flex: 1;
                `}
              >
                {secondHalfFAQs.map((item: any, index: number) => (
                  <FAQItem
                    key={index}
                    question={item.question}
                    answer={item.answer.json}
                  />
                ))}
              </div>
            </div>
          </div>
        </Container>
        <Container
          css={css`
            margin-bottom: 60px;
            ${mq["lg"]} {
              margin-bottom: 120px;
            }
          `}
        >
          <HelpSection />
        </Container>
      </Layout>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const { fields } = await getDemoPage();
  return { props: { fields } };
};

export default Page;
